import { EdlinkIdCode } from '@/components/alphanumeric-code.js';
import { ArrowTooltip } from '@/components/arrow-tooltip.js';
import { EditableInfoCard, SubmitProps } from '@/components/editable-info-card.js';
import { partitionSessionFeedbackIssues } from '@/components/feedbacks/feedback-issues.js';
import { FormCheckbox } from '@/components/forms/form-checkbox.js';
import { EditUserIdentitiesForm, SsoInfoCard } from '@/components/sso/sso-info-card.js';
import { FormComboBox } from '@/components/forms/form-combo-box.js';
import { FormInputField } from '@/components/forms/form-input-field.js';
import { FormMultiSelect } from '@/components/forms/form-multi-select.js';
import { FormSingleSelect } from '@/components/forms/form-single-select.js';
import { InfoCard } from '@/components/info-card.js';
import { InfoKeyValue } from '@/components/info-key-value.js';
import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';
import { StudentShortLinks } from '@/components/join-now/student-short-links.js';
import { StudentStatusBadge } from '@/components/students/student-status-badge.js';
import { StudentTagBadgeList } from '@/components/students/student-tag-badge.js';
import { comboBoxOptionsOf, emptySelectOption, requiredSelectOption } from '@/components/ui/multi-select-utils.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import {
  getMinimalSchools,
  getStudent,
  updateStudentProfile,
  regenerateShortLink,
  syncStudentFromEdlink,
  dryRunSyncStudentFromEdlink,
} from '@/lib/api-calls.js';
import { SyncEdlinkModal } from '@/components/edlink/sync-edlink-modal.js';
import { filterByStartInInterval } from '@/lib/date-utils.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { nullIfString, stringIfNull } from '@/lib/string-utils.js';
import { studentFeedbacksSearchSchema } from '@/lib/student-feedback-utils.js';
import {
  allStudentStatuses,
  buildStudentTagSelectOptions,
  calculateAttendanceRate,
  getStudentStatusLabel,
  studentTagOptions,
} from '@/lib/student-utils.js';
import { StudentFeedbacksTable } from '@/routes/_layout/students/-components/student-feedbacks-table.js';
import { InstantInterval, isAirAdmin, isStudentRole, studentOfSchool } from '@airreading/common';
import { Temporal } from 'temporal-polyfill';
import {
  DEPRECATED_allPagesModifier,
  SessionFeedbackIssue,
  StudentContactType,
  SessionFeedbackIssueDetails,
  StudentSessionFeedbackLike,
  studentStatusSchema,
  StudentTag,
  studentYearOption,
  studentYearSelectableSchema,
  studentYearValues,
  UserRoleAssignmentOptions,
} from '@airreading/common/data';
import { multiSelectOptions } from '@/components/ui/multi-select-utils.js';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useId, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { CreateStudentFeedback } from '../-components/create-student-feedback.js';
import { SchoolWithDistrict } from '../-components/school-with-district.js';
import { studentTabs } from '../-components/tabs.js';
import { validateRouteRole } from '@/lib/routing.js';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  DialogHeader,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog.js';
import { RotateCcwKey } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import { cn } from '@/lib/tailwind-utils.js';

export const Route = createFileRoute('/_layout/students/$studentId/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentPage,
  validateSearch: studentFeedbacksSearchSchema,
});

const fleidSchema = z
  .string()
  .optional()
  .nullable()
  .refine(
    (val) => {
      if (!val || val.trim() === '') return true; // Allow empty/null
      return /^[Ff][Ll]\d{12}$/.test(val.trim());
    },
    { message: 'FLEID must be formatted as FL followed by 12 digits (e.g., FL000010437354)' },
  )
  .transform((val) => (val?.trim() ? val.trim().toUpperCase() : null));

const editDetailsInfoFormSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().nullable().optional().default(null),
  timeZone: z.string().optional(),
  status: studentStatusSchema.optional().nullable(),
  email: z.string().email({ message: 'Invalid email' }).optional().or(z.literal('')),
  fleid: fleidSchema,
  studentTags: multiSelectOptions,
  parentEmail: z.string().email({ message: 'Invalid email' }).optional().or(z.literal('')),
  parentPhone: z.string().optional().or(z.literal('')),
  edlinkId: z.string().optional().nullable().default(null),
});
type EditDetailsInfoForm = z.infer<typeof editDetailsInfoFormSchema>;

const editAcademicsInfoSchema = z.object({
  grade: studentYearSelectableSchema,
  schoolId: requiredSelectOption('School selection is required'),
  specialEd: z.boolean().default(false).optional(),
  dyslexic: z.boolean().default(false).optional(),
  ell: z.boolean().default(false).optional(),
  classroomTeacher: z.string().optional().nullable(),
});
type EditAcademicsInfoForm = z.infer<typeof editAcademicsInfoSchema>;

const districtAutoUpdateHint = "District is automatically updated when a student's School is changed.";

function AcademicsSchoolAndDistrict({
  form,
  editing,
  currentSchool,
}: {
  form: UseFormReturn<EditAcademicsInfoForm>;
  editing: boolean;
  currentSchool: { id: string; name: string } | null;
}) {
  const districtInputId = useId();
  const schoolId = form.watch('schoolId')?.value;
  const { data: schoolsResponse, isLoading: isLoadingSchools } = useQuery(
    getMinimalSchools(DEPRECATED_allPagesModifier),
  );
  const schools = schoolsResponse?.schools;
  const schoolOptions = useMemo(
    () =>
      (schools ?? [])
        .map((school) => ({
          label: `${school.name} ${school.org.name}`,
          value: school.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [schools],
  );
  const selectedSchool = schools?.find((school) => school.id === schoolId);
  const districtName = selectedSchool?.org.name ?? '';

  return (
    <>
      <InfoKeyValue
        className="w-1/4"
        label="School"
        value={currentSchool?.name}
        data-testid="students-academics-school"
        edit={{
          editing,
          editModeComponent: (
            <FormComboBox
              className="mt-0 w-1/4"
              name="schoolId"
              label="School"
              form={form}
              placeholder="Select a school"
              {...comboBoxOptionsOf(schoolOptions)}
              renderOption={(option) => {
                const school = schools?.find((item) => item.id === option?.value);
                return school ? <SchoolWithDistrict school={school} /> : option?.label;
              }}
              renderOptionSelected={(option) => {
                const school = schools?.find((item) => item.id === option?.value);
                return school ? <SchoolWithDistrict school={school} /> : option?.label || 'Select a school';
              }}
              emptySearchOptionMessage="No schools found"
              isLoadingKnownOptions={isLoadingSchools}
              triggerProps={{ className: 'w-2/3', 'data-testid': 'students-academics-select-school' }}
            />
          ),
        }}
      />
      <InfoKeyValue
        className="w-1/4"
        label="District"
        data-testid="students-academics-district"
        value={!schoolId ? '-' : isLoadingSchools ? <span role="status">Loading district</span> : districtName || '-'}
        edit={{
          editing,
          editModeComponent: (
            <div className="w-1/4 space-y-2">
              <Label htmlFor={districtInputId} className="font-bold">
                District
              </Label>
              <ArrowTooltip
                asChild
                trigger={
                  <span
                    className="flex w-2/3 cursor-not-allowed"
                    tabIndex={0}
                    aria-label="District"
                    aria-describedby={`${districtInputId}-hint`}
                  >
                    <Input
                      id={districtInputId}
                      value={districtName}
                      disabled
                      readOnly
                      className="w-full pointer-events-none"
                      placeholder={isLoadingSchools ? 'Loading district' : undefined}
                      aria-busy={isLoadingSchools || undefined}
                      aria-describedby={`${districtInputId}-hint`}
                      data-testid="students-academics-input-district"
                    />
                  </span>
                }
              >
                {districtAutoUpdateHint}
              </ArrowTooltip>
              <span id={`${districtInputId}-hint`} className="sr-only">
                {districtAutoUpdateHint}
              </span>
              {isLoadingSchools && (
                <span className="sr-only" role="status">
                  Loading district
                </span>
              )}
            </div>
          ),
        }}
      />
    </>
  );
}

function StudentPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load student. Please try again.',
        };
      }}
    >
      <StudentDetail />
    </InfoPageSuspenseBoundary>
  );
}

function getAttendanceValues(sessionFeedbacks: StudentSessionFeedbackLike[] | null, daysAgo: number) {
  const pastNDaysAttendance = calculateAttendanceRate(sessionFeedbacks, daysAgo);

  // Calculate session counts from feedback data
  const pastNDaysFeedbacks = !daysAgo
    ? sessionFeedbacks || []
    : filterByStartInInterval(sessionFeedbacks || [], InstantInterval.untilNow({ hours: daysAgo * 24 }));

  const pastNDaysSessionsAttended = pastNDaysFeedbacks.filter((feedback) => feedback.studentAttended === true);
  const pastNDaysSessions = pastNDaysFeedbacks; // All feedbacks represent expected sessions

  return {
    pastNDaysAttendance,
    pastNDaysSessions,
    pastNDaysSessionsAttended,
  };
}

function getFeedbackIssues<F extends StudentSessionFeedbackLike>(sessionFeedbacks: F[], daysAgo: number) {
  const pastNDaysSessionFeedbacks = filterByStartInInterval(
    sessionFeedbacks,
    InstantInterval.untilNow({ hours: daysAgo * 24 }),
  );
  const sessionFeedbackIssues = pastNDaysSessionFeedbacks.flatMap(({ issues }) => issues) as SessionFeedbackIssue[];
  return partitionSessionFeedbackIssues(sessionFeedbackIssues);
}

function StudentDetail() {
  const { studentId } = Route.useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: student } = useSuspenseQuery(getStudent(studentId));

  const {
    pastNDaysAttendance: past90DaysAttendance,
    pastNDaysSessions: past90DaysSessions,
    pastNDaysSessionsAttended: past90DaysSessionsAttended,
  } = useMemo(() => getAttendanceValues(student.sessionFeedbacks, 90), [student.sessionFeedbacks]);

  const {
    pastNDaysAttendance: past14DaysAttendance,
    pastNDaysSessions: past14DaysSessions,
    pastNDaysSessionsAttended: past14DaysSessionsAttended,
  } = useMemo(() => getAttendanceValues(student.sessionFeedbacks, 14), [student.sessionFeedbacks]);

  const past30DaysUniqueSessionTeachers = useMemo(() => {
    const sessions = filterByStartInInterval(
      student.studentSessions || [],
      InstantInterval.untilNow({ hours: 30 * 24 }),
    );
    const coaches = [...new Set(sessions.map(({ coachId }) => coachId))];
    return coaches.length;
  }, [student.studentSessions]);

  const { behaviorIssues, learningIssues, technicalIssues } = useMemo(
    () => getFeedbackIssues(student.sessionFeedbacks || [], 7),
    [student.sessionFeedbacks],
  );

  // Calculate the most recently completed lesson from student sessions
  const currentLesson = useMemo(() => {
    if (!student.studentSessions || student.studentSessions.length === 0) {
      return student.startingLesson?.name || 'No lessons completed';
    }

    // Sort student sessions by end time (most recent first)
    const sortedSessions = [...student.studentSessions].sort((a, b) =>
      Temporal.Instant.compare(b.endInstant, a.endInstant),
    );

    // Find the most recent completed session (where student joined)
    const mostRecentSession = sortedSessions.find((session) => session.joined === true);

    if (mostRecentSession && mostRecentSession.lesson) {
      return mostRecentSession.lesson.name;
    }

    // Fallback to starting lesson if no completed sessions found
    return student.startingLesson?.name || 'No lessons completed';
  }, [student.studentSessions, student.startingLesson]);

  const { mutateAsync: updateStudentMutate } = useMutation({ mutationFn: updateStudentProfile });

  const didCatchError = useCallback(
    (title: string, e: unknown) => {
      const error = airServerErrorFrom(e);
      if (error) {
        toast({
          variant: 'destructive',
          title,
          description: error.userFacingMessage,
        });
      }
    },
    [toast],
  );

  const onSubmitDetailsInfo = ({ formData, disableEditing }: SubmitProps<EditDetailsInfoForm>) => {
    const { status, fleid, studentTags, parentEmail, parentPhone, edlinkId, email, ...rest } = formData;
    const newEmail = email?.trim().length === 0 ? null : email;
    return updateStudentMutate({
      userId: studentId,
      data: {
        user: { ...rest, email: newEmail },
        status,
        fleid,
        edlinkId: edlinkId?.trim() ? edlinkId : null,
        studentTags: studentTags.map((option) => option.value as StudentTag),
        studentContacts: [
          {
            type: StudentContactType.school_parent,
            email: parentEmail,
            phone: parentPhone,
          },
        ],
      },
    })
      .then(() => {
        toast({ title: 'Student updated successfully' });
        disableEditing();
        queryClient.invalidateQueries(getStudent(studentId));
      })
      .catch((e) => didCatchError('Student update failed', e));
  };

  const onSubmitSSOInfo = ({ formData, disableEditing }: SubmitProps<EditUserIdentitiesForm>) => {
    return updateStudentMutate({
      userId: studentId,
      data: { userIdentities: formData },
    })
      .then(() => {
        toast({ title: 'Student updated successfully' });
        disableEditing();
        queryClient.invalidateQueries(getStudent(studentId));
      })
      .catch((e) => didCatchError('Student update failed', e));
  };

  const onSubmitAcademicsInfo = ({ formData, disableEditing }: SubmitProps<EditAcademicsInfoForm>) => {
    const roles: UserRoleAssignmentOptions = student.userRoles?.filter((role) => !isStudentRole(role)) || [];
    roles.push(studentOfSchool(formData.schoolId.value));

    return updateStudentMutate({
      userId: studentId,
      data: {
        roles,
        grade: nullIfString(formData.grade),
        specialEd: formData.specialEd,
        dyslexic: formData.dyslexic,
        ell: formData.ell,
        classroomTeacher: formData.classroomTeacher?.trim() ? formData.classroomTeacher.trim() : null,
      },
    })
      .then(() => {
        toast({ title: 'Student updated successfully' });
        disableEditing();
        queryClient.invalidateQueries(getStudent(studentId));
      })
      .catch((e) => didCatchError('Student update failed', e));
  };

  const StudentShortLinkDialog = ({ editing, studentId }: { editing: boolean; studentId: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const { mutateAsync: regenerateShortLinkMutate, isPending } = useMutation({
      mutationFn: regenerateShortLink,
    });

    const handleRegenerate = async () => {
      try {
        await regenerateShortLinkMutate(studentId);
        toast({
          title: 'Short link regenerated successfully',
          description: 'A new link has been created and the old link has been deactivated.',
        });
        queryClient.invalidateQueries(getStudent(studentId));
        setIsOpen(false);
      } catch (e) {
        const error = airServerErrorFrom(e);
        toast({
          variant: 'destructive',
          title: 'Failed to regenerate short link',
          description: error?.userFacingMessage || 'An error occurred while regenerating the short link.',
        });
      }
    };

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <button type="button" className="cursor-pointer" disabled={editing}>
            <RotateCcwKey className={cn('w-4 h-4', editing && 'opacity-50 cursor-not-allowed')} />
          </button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate Link</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              The current link will be immediately deactivated. A new link will be created. Anyone using the old link
              will no longer have access.
            </p>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary" onClick={() => setIsOpen(false)} disabled={isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRegenerate}
              loading={isPending}
              disabled={isPending}
            >
              Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div className="mb-2">
      <InfoPage
        headerText={student.fullNameOrEmail}
        tabs={studentTabs({ studentId })}
        headerActions={
          student.edlinkId ? (
            <SyncEdlinkModal
              modalTitle="Sync Edlink student"
              entityName={student.fullNameOrEmail ?? ''}
              entityId={student.edlinkId}
              trigger={<Button>Sync from Edlink</Button>}
              syncFn={() => syncStudentFromEdlink.queryFn(studentId)}
              syncSentryTags={{ feature: 'edlink-sync-student', studentId }}
              syncErrorMessage="Edlink student sync failed. Please try again or contact support."
              onSyncSuccess={() => queryClient.invalidateQueries(getStudent(studentId))}
              dryRunFn={() => dryRunSyncStudentFromEdlink(studentId)}
              dryRunSentryTags={{ feature: 'edlink-sync-student-dry-run', studentId }}
            />
          ) : undefined
        }
        breadcrumbs={{
          linkItems: [
            {
              label: 'Students',
              link: {
                to: '/students',
              },
            },
          ],
          currentPage: student.fullNameOrEmail ?? '',
        }}
      >
        <>
          <EditableInfoCard
            key={`student-details-${studentId}-${JSON.stringify(student.studentTags)}`}
            headerText="Student details"
            formSchema={editDetailsInfoFormSchema}
            onSubmit={onSubmitDetailsInfo}
            defaultValues={{
              ...student,
              firstName: student.firstName || undefined,
              lastName: student.lastName || undefined,
              timeZone: student.timeZone || undefined,
              status: student.status || undefined,
              email: student.email || undefined,
              fleid: student.fleid || undefined,
              studentTags: buildStudentTagSelectOptions(student.studentTags),
              parentEmail: student.studentContacts?.[0]?.email || undefined,
              parentPhone: student.studentContacts?.[0]?.phone || undefined,
              edlinkId: student.edlinkId || undefined,
            }}
          >
            {({ form, editing }) => (
              <div className="flex flex-col">
                <div className="grid grid-cols-4 grid-rows-2 gap-4 pb-4">
                  <InfoKeyValue
                    label="First name"
                    value={student.firstName}
                    edit={{
                      editing,
                      editModeComponent: <FormInputField name="firstName" label="First Name" form={form} />,
                    }}
                  />
                  <InfoKeyValue
                    label="Last name"
                    value={student.lastName}
                    edit={{
                      editing,
                      editModeComponent: <FormInputField name="lastName" label="Last Name" form={form} />,
                    }}
                  />
                  <InfoKeyValue label="Time zone" value={student.timeZone} />
                  <InfoKeyValue
                    label="Status"
                    value={<StudentStatusBadge status={student.status} />}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormSingleSelect
                          name="status"
                          label="Status"
                          form={form}
                          options={allStudentStatuses}
                          renderOption={getStudentStatusLabel}
                          triggerProps={{ className: 'w-2/3' }}
                        />
                      ),
                    }}
                  />
                  <InfoKeyValue
                    label="Email"
                    value={student.email || '-'}
                    edit={{
                      editing,
                      editModeComponent: <FormInputField name="email" label="Email" form={form} />,
                    }}
                    className="break-all"
                  />
                  <InfoKeyValue
                    label="FLEID"
                    value={student.fleid || '-'}
                    edit={{
                      editing,
                      editModeComponent: <FormInputField name="fleid" label="FLEID" form={form} />,
                    }}
                  />
                  <InfoKeyValue
                    label="Student tag"
                    value={<StudentTagBadgeList tags={student.studentTags} />}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormMultiSelect
                          name="studentTags"
                          label="Student tag"
                          form={form}
                          multipleSelectorProps={{
                            options: studentTagOptions,
                            placeholder: 'Select tags...',
                          }}
                        />
                      ),
                    }}
                  />
                  <InfoKeyValue
                    className="min-w-0"
                    label={
                      <div className="flex items-center gap-2">
                        Direct join short link
                        <StudentShortLinkDialog editing={editing} studentId={studentId} />
                      </div>
                    }
                    value={
                      <div className="min-w-0 space-y-2">
                        <StudentShortLinks shortLinks={student.shortLinks} shortCode={student.shortCode} />
                      </div>
                    }
                  />
                  <InfoKeyValue
                    label="School parent email"
                    value={student.studentContacts?.[0]?.email || '-'}
                    edit={{
                      editing,
                      editModeComponent: <FormInputField name="parentEmail" label="School parent email" form={form} />,
                    }}
                    className="break-all"
                  />
                  <InfoKeyValue
                    label="School parent phone number"
                    value={student.studentContacts?.[0]?.phone || '-'}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormInputField name="parentPhone" label="School parent phone number" form={form} />
                      ),
                    }}
                    className="break-all"
                  />
                  <InfoKeyValue
                    label="Edlink ID"
                    value={student.edlinkId ? <EdlinkIdCode code={student.edlinkId} /> : '-'}
                    edit={{
                      editing,
                      editModeComponent: <FormInputField name="edlinkId" label="Edlink ID" form={form} />,
                    }}
                  />
                </div>
              </div>
            )}
          </EditableInfoCard>
          <SsoInfoCard user={student} onSubmit={onSubmitSSOInfo} />
          <EditableInfoCard
            headerText="Academics"
            formSchema={editAcademicsInfoSchema}
            onSubmit={onSubmitAcademicsInfo}
            defaultValues={{
              grade: stringIfNull(student.grade),
              schoolId: student.school ? { label: student.school.name, value: student.school.id } : emptySelectOption,
              specialEd: student.specialEd || undefined,
              dyslexic: student.dyslexic || undefined,
              ell: student.ell || undefined,
              classroomTeacher: student.classroomTeacher ?? '',
            }}
          >
            {({ form, editing }) => (
              <div className="flex flex-col gap-6">
                <div className="flex flex-row">
                  <InfoKeyValue
                    className="w-1/4"
                    label="Grade"
                    value={studentYearOption(stringIfNull(student.grade))}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormSingleSelect
                          className="w-1/4"
                          name="grade"
                          label="Grade"
                          form={form}
                          options={studentYearValues}
                          renderOption={studentYearOption}
                          triggerProps={{ className: 'w-2/3' }}
                        />
                      ),
                    }}
                  />
                  <AcademicsSchoolAndDistrict form={form} editing={editing} currentSchool={student.school} />
                  <InfoKeyValue
                    className="w-1/4"
                    label="Classroom teacher"
                    value={student.classroomTeacher ?? '-'}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormInputField
                          className="w-1/4"
                          name="classroomTeacher"
                          label="Classroom teacher"
                          form={form}
                        />
                      ),
                    }}
                  />
                </div>
                <div className="flex flex-row">
                  <InfoKeyValue
                    className="w-1/4"
                    label="Special Ed"
                    value={student.specialEd ? 'Yes' : 'No'}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormCheckbox className="w-1/4" name="specialEd" form={form} label="Special Ed" />
                      ),
                    }}
                  />
                  <InfoKeyValue
                    className="w-1/4"
                    label="ELL"
                    value={student.ell ? 'Yes' : 'No'}
                    edit={{
                      editing,
                      editModeComponent: <FormCheckbox className="w-1/4" name="ell" form={form} label="ELL" />,
                    }}
                  />
                  <InfoKeyValue
                    className="w-1/4"
                    label="Dyslexic"
                    value={student.dyslexic ? 'Yes' : 'No'}
                    edit={{
                      editing,
                      editModeComponent: (
                        <FormCheckbox className="w-1/4" name="dyslexic" form={form} label="Dyslexic" />
                      ),
                    }}
                  />
                  <div className="w-1/4" />
                </div>
              </div>
            )}
          </EditableInfoCard>
          <InfoCard headerText="Attendance">
            <div className="flex flex-col gap-6">
              <div>
                <span className="text-sm text-gray-500">Past 90 days</span>
                <div className="flex flex-row pt-2">
                  <InfoKeyValue className="w-1/3" label="Attendance percentage" value={`${past90DaysAttendance}%`} />
                  <InfoKeyValue
                    className="w-1/3"
                    label="Number of sessions attended"
                    value={past90DaysSessionsAttended.length}
                  />
                  <InfoKeyValue
                    className="w-1/3"
                    label="Number of sessions attended expected"
                    value={past90DaysSessions.length}
                  />
                </div>
              </div>
              <div>
                <span className="text-sm text-gray-500">Past 14 days</span>
                <div className="flex flex-row pt-2">
                  <InfoKeyValue className="w-1/3" label="Attendance percentage" value={`${past14DaysAttendance}%`} />
                  <InfoKeyValue
                    className="w-1/3"
                    label="Number of sessions attended"
                    value={past14DaysSessionsAttended.length}
                  />
                  <InfoKeyValue
                    className="w-1/3"
                    label="Number of sessions attended expected"
                    value={past14DaysSessions.length}
                  />
                </div>
              </div>
              <InfoKeyValue
                label="Number of unique session teachers in the past 30 days"
                value={past30DaysUniqueSessionTeachers}
              />
            </div>
          </InfoCard>
          <InfoCard headerText="Learning">
            <InfoKeyValue label="Current lesson" value={currentLesson} />
            <InfoKeyValue label="Behavior issue - past 7 days" value={behaviorIssues.length} />
            <InfoKeyValue label="Tech issue - past 7 days" value={technicalIssues.length} />
            <InfoKeyValue
              label="Learning issue"
              value={[...new Set(learningIssues)].map((issue) => SessionFeedbackIssueDetails[issue].label).join(', ')}
            />
          </InfoCard>
          <InfoCard headerText="Review notes" headerActions={<CreateStudentFeedback studentId={studentId} />}>
            <StudentFeedbacksTable route={Route} studentId={studentId} />
          </InfoCard>
        </>
      </InfoPage>
    </div>
  );
}
